import React, { FC, useState } from "react";
import { mergeDeepRight } from "ramda";
import classNames from "classnames/dedupe";
import MediaBuilder from "../../components/Utility/MediaBuilder/MediaBuilder";
import { Typography } from "../../components/Fundamentals/Typography";
import {
  Button,
  ScrollContainer,
  ShoppingBagItem,
  SvgIcon,
  Select,
} from "../../components";
import {
  mockColorOptions,
  mockFootSizesOptions,
  shoppingBagItems,
} from "../../mock/shoppingBagData";
import deepClone from "../../helpers/deepClone";
import Divider from "../../components/DataDisplay/Divider/Divider";
import Alert from "../../components/DataDisplay/Alert/Alert";
import TextInput from "../../components/ActionsSelectors/Input/TextInput/TextInput";

const initialStoreState = {
  items: {
    [shoppingBagItems[0].id]: {
      size: shoppingBagItems[0].size,
      color: shoppingBagItems[0].color,
    },
    [shoppingBagItems[1].id]: {
      size: shoppingBagItems[1].size,
      initials: "",
    },
  },
  sole: "",
};

const EditBundleTemplate: FC = () => {
  const [form, setForm] = useState(() => deepClone(initialStoreState));
  const [selected, setSelected] = useState(0);
  return (
    <div className="container-fluid bundle d-flex min-w-0 px-6">
      <div className="bundle__content d-none d-lg-flex align-items-center justify-content-center flex-grow-1 p-4">
        <div className="bg-color-grey-200 d-block">
          <div className="d-flex align-items-center justify-content-center">
            <MediaBuilder
              data={{
                classes: { root: "bundle__image w-100" },
                src: "assets/images/bundle/main.jpg",
              }}
            />
          </div>
        </div>
      </div>
      <div className="bundle__sidebar d-flex flex-column min-w-0">
        <div className="d-flex justify-content-between align-items-center w-100">
          <Typography
            variant="mobile-header-12-bold"
            classes={{ root: "text-uppercase ms-lg-6 me-4" }}
          >
            Customize items & add to bag
          </Typography>
          <div className="p-5 my-3 me-n6 cursor-pointer text-color-blue text-color-pressed-white-hover transition-color">
            <SvgIcon icon="ExitCrossIcon" />
          </div>
        </div>
        <ScrollContainer
          classes={{
            root: classNames(
              "uds-scroll--grey uds-scroll--minus-me-5 pe-4 mh-none",
            ),
          }}
        >
          <div
            className="position-relative mb-6 mb-lg-7"
            onClick={() => setSelected(0)}
          >
            <Divider
              size="bold"
              color="dark-blue"
              dir="vertical"
              classes={{
                root: classNames(
                  "d-none position-absolute left-0 top-0 bottom-0",
                  {
                    "d-lg-block": selected === 0,
                  },
                ),
              }}
            />
            <div className="ms-lg-5">
              <ShoppingBagItem
                classes={{ root: "p-0" }}
                type="mini"
                readonly
                {...shoppingBagItems[0]}
                productImage={{ src: "assets/images/bundle/thumbnails/1.png" }}
              />
              <div className="my-4 my-lg-6">
                <Select
                  value={form.items[shoppingBagItems[0].id].size}
                  options={mockFootSizesOptions}
                  placeholder="Select size"
                  noOptionsPlaceholder="No sizes available"
                  titlePrefix="Size - "
                  valueKey="label"
                  onChange={(newValue) =>
                    setForm(
                      mergeDeepRight(form, {
                        items: {
                          [shoppingBagItems[0].id]: {
                            size: newValue,
                          },
                        },
                      }),
                    )
                  }
                />
              </div>
              <div className="my-4 my-lg-6">
                <Select
                  value={form.items[shoppingBagItems[0].id].color}
                  options={mockColorOptions}
                  placeholder="Select color"
                  titlePrefix="Color - "
                  noOptionsPlaceholder="No colors available"
                  valueKey="label"
                  onChange={(newValue) =>
                    setForm(
                      mergeDeepRight(
                        form as any,
                        {
                          items: {
                            [shoppingBagItems[0].id]: {
                              color: newValue,
                            },
                          },
                        } as any,
                      ),
                    )
                  }
                />
              </div>
              <Divider
                color="grey"
                dir="horizontal"
                classes={{ root: "mt-4 mt-lg-6 mb-5 mb-lg-7" }}
              />
            </div>
          </div>
          <div
            className="position-relative my-6 my-lg-7"
            onClick={() => setSelected(1)}
          >
            <Divider
              size="bold"
              color="dark-blue"
              dir="vertical"
              classes={{
                root: classNames(
                  "d-none position-absolute left-0 top-0 bottom-0",
                  {
                    "d-lg-block": selected === 1,
                  },
                ),
              }}
            />
            <div className="ms-lg-5">
              <ShoppingBagItem
                classes={{ root: "p-0" }}
                type="mini"
                readonly
                {...shoppingBagItems[1]}
                productImage={{ src: "assets/images/bundle/thumbnails/2.jpg" }}
              />
              <div className="my-4 my-lg-6">
                <Select
                  classes={{ button: "m-0 d-flex" }}
                  value={form.items[shoppingBagItems[1].id].size}
                  options={mockFootSizesOptions}
                  placeholder="Select size"
                  noOptionsPlaceholder="No sizes available"
                  titlePrefix="Size - "
                  valueKey="label"
                  onChange={(newValue) =>
                    setForm(
                      mergeDeepRight(form, {
                        items: {
                          [shoppingBagItems[1].id]: {
                            size: newValue,
                          },
                        },
                      }),
                    )
                  }
                />
              </div>

              <div className="mt-5 mb-4 mb-lg-6">
                <TextInput
                  label="Initials"
                  placeholder=" "
                  type="text"
                  value={form.items[shoppingBagItems[1].id].initials}
                  onChange={(event) =>
                    setForm(
                      mergeDeepRight(
                        form as any,
                        {
                          items: {
                            [shoppingBagItems[1].id]: {
                              initials: event.target.value,
                            },
                          },
                        } as any,
                      ),
                    )
                  }
                />
                <Typography
                  as="div"
                  variant="text-small"
                  classes={{
                    root: "font-weight-regular text-color-grey-600 mt-4 mb-6",
                  }}
                >
                  (Optional) + 40 kr
                </Typography>
              </div>
              <Divider
                color="grey"
                dir="horizontal"
                classes={{ root: "mt-4 mt-lg-6 mb-5 mb-lg-7" }}
              />
            </div>
          </div>
          <div className="position-relative mt-6 mt-lg-7">
            <Divider
              size="bold"
              color="dark-blue"
              dir="vertical"
              classes={{
                root: classNames(
                  "d-none position-absolute left-0 top-0 bottom-0",
                  {
                    "d-lg-block": selected === 2,
                  },
                ),
              }}
            />
            <div className="ms-lg-5">
              <ShoppingBagItem
                classes={{ root: "p-0" }}
                type="mini"
                readonly
                {...shoppingBagItems[2]}
                productImage={{ src: "assets/images/bundle/thumbnails/3.jpg" }}
              />
              <Divider
                color="grey"
                dir="horizontal"
                classes={{ root: "mt-4 mt-lg-6 mb-5 mb-lg-7" }}
              />
            </div>
          </div>
          <div className="position-relative mt-6 mt-lg-7">
            <Divider
              size="bold"
              color="dark-blue"
              dir="vertical"
              classes={{
                root: classNames(
                  "d-none position-absolute left-0 top-0 bottom-0",
                  {
                    "d-lg-block": selected === 3,
                  },
                ),
              }}
            />
            <div className="ms-lg-5">
              <ShoppingBagItem
                classes={{ root: "p-0" }}
                type="mini"
                readonly
                {...shoppingBagItems[2]}
                productImage={{ src: "assets/images/bundle/thumbnails/3.jpg" }}
              />
            </div>
          </div>
        </ScrollContainer>
        <div className="ms-lg-5">
          <Divider
            classes={{
              root: "mt-4 mt-lg-6 mb-5 mb-lg-7",
            }}
          />
          <div className="mt-3 mt-lg-4 mb-4 mb-lg-5">
            <Alert
              isWarning
              headerContent={
                <>
                  <SvgIcon icon="WarningIcon" />
                  <span>Please enter your sole length for pre-assembly</span>
                </>
              }
              bodyContent="We send the ski pre-assembled. Before you start your first
                  ride, the Z value on your safety binding must be adjusted. The
                  Z value depends on your height, weight, age, ski boot length
                  and skiing ability."
            />
          </div>
          <TextInput
            classes={{ root: "my-4 mt-lg-5 mb-lg-6" }}
            label="Sole length in mm"
            placeholder=" "
            type="text"
            value={form.sole}
            onChange={(event) =>
              setForm(
                mergeDeepRight(form, {
                  sole: event.target.value,
                }),
              )
            }
          />
          <div className="mt-6 mt-lg-7 mb-5 mb-lg-6">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-baseline">
                <Typography
                  variant="text-bold-header"
                  classes={{ root: "text-uppercase me-3" }}
                >
                  Total
                </Typography>
                <Typography
                  variant="text-small"
                  classes={{ root: "font-weight-regular" }}
                >
                  3 items
                </Typography>
              </div>
              <Typography variant="text-bold-header">€ 305.87</Typography>
            </div>
          </div>
          <div className="my-5">
            <Button variant="conversion" classes={{ root: "w-100" }}>
              add all to bag
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBundleTemplate;
